import * as React from 'react'
import { styled, SxProps, Theme } from '@mui/material/styles'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { FormHelperText } from '@mui/material'

interface CustomSelectProps {
  className?: string
  name?: string
  iconComponent?: React.ElementType
  label: string
  onChange: (event: SelectChangeEvent<unknown>, child: React.ReactNode) => void
  options: Array<{ value: string | number; label: string | number }>
  sx?: SxProps<Theme>
  value: string
  error?: boolean
  helperText?: string
}

const CustomMenuItem = styled(MenuItem)(() => ({
  color: '#7D7D7D',
  display: 'flex',
  justifyContent: 'center',
  textAlign: 'center',
}))

const CustomInputLabel = styled(InputLabel)(() => ({
  color: '#7D7D7D',
  padding: '12px',
  fontSize: '16px',
  '&.Mui-focused': {
    color: '#7D7D7D',
    fontSize: '16px',
  },
}))

const CustomSelectStyled = styled(Select)(() => ({
  padding: '1.2px',
  height: '80px',
  backgroundColor: 'rgb(8,12,20,0.05)',
  borderColor: '#365F91',
  border: '1.2px solid transparent',
  background: `
    linear-gradient(rgb(8, 12, 20), rgb(8, 12, 20)) padding-box,
    linear-gradient(180deg, #365f91 30%, #365f9133 100%) border-box
  `,
  borderImageSlice: 1,
  borderRadius: '40px',

  '&:before': {
    borderBottom: 'none !important',
    backgroundColor: 'transparent !important',
  },
  '&:after': {
    borderBottom: 'none !important',
    backgroundColor: 'transparent !important',
  },
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '& .MuiInputBase-input': {
    color: 'white',
    textAlign: 'left',
    paddingLeft: '20px',
    backgroundColor: 'none',
    outline: 'none',
  },
  '& .MuiSelect-icon': {
    color: '#7D7D7D',
    margin: '0 6px',
  },
}))

export default function FormSelect({
  className,
  label,
  name,
  value,
  error,
  helperText,
  onChange,
  options,
  sx,
  iconComponent,
}: CustomSelectProps) {
  return (
    <FormControl variant="filled" sx={{ ...sx }}>
      {value ? null : (
        <CustomInputLabel id="custom-select-label" className="text-center">
          {label}
        </CustomInputLabel>
      )}
      <CustomSelectStyled
        labelId="custom-select-label"
        id="custom-select"
        error={!!error}
        value={value}
        name={name}
        onChange={onChange}
        label={label}
        className={className}
        sx={sx}
        IconComponent={iconComponent ? iconComponent : ExpandMoreOutlinedIcon}
        MenuProps={{
          PaperProps: {
            sx: {
              bgcolor: 'black',
              borderRadius: '8px',
              textAlign: 'center',
              '& .MuiMenu-list': {
                padding: '0',
                bgcolor: 'rgba(54, 95, 145, 0.3)',
                textAlign: 'center',
                backdropFilter: 'blur(16px)',
                webkitBackdropFilter: 'blur(16px)',
              },
              '& .MuiMenuItem-root': {
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(251, 80, 18, 0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: '#FB5012 !important',
                  color: 'white',
                },
                '&.MuiSelect-select': {
                  textAlign: 'center',
                },
              },
            },
          },
        }}
      >
        {options.map((option) => (
          <CustomMenuItem key={option.value} value={option.value}>
            {option.label}
          </CustomMenuItem>
        ))}
      </CustomSelectStyled>
      <FormHelperText
        error
        sx={{
          height: '6px',
          marginLeft: '14px',
          marginTop: '4px',
          color: '#f44336',
        }}
      >
        {error && helperText && helperText}
      </FormHelperText>
    </FormControl>
  )
}
